import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import Button from './Button';

function LegalDialog({ show, onClose, title, intro, sections }) {
  useEffect(() => {
    if (!show) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, show]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-dialog-title"
    >
      <motion.div
        initial={{ opacity: 0, y: 18, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="max-h-[86vh] w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-950/20"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div>
            <p className="eyebrow">Clear Horizons</p>
            <h2 id="legal-dialog-title" className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="focus-ring flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-950"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[56vh] space-y-6 overflow-y-auto px-6 py-6">
          <p className="text-base leading-7 text-slate-600">{intro}</p>
          {sections.map((section) => (
            <section key={section.title}>
              <h3 className="text-base font-semibold text-slate-950">{section.title}</h3>
              <p className="mt-2 text-sm leading-7 text-slate-600">{section.copy}</p>
            </section>
          ))}
        </div>

        <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">
          <Button as="button" type="button" onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

export default LegalDialog;
